import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Card, IconButton, useTheme, Divider } from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';
import PageLayout from '../components/PageLayout';
import StyledTable from '../components/StyledTable';
import { AuthContext } from '../context/AuthContext.jsx';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [records, setRecords] = useState([]);
  const [clientDates, setClientDates] = useState([]);
  const [clientName, setClientName] = useState('');
  const [submittals, setSubmittals] = useState([]);
  const { userMetadata } = useContext(AuthContext);
  useEffect(() => {
    const fetchSubmittals = async () => {
      const { data, error } = await supabase
        .from('submittal_schedule')
        .select(`
          id,
          name,
          deadline,
          submittal_date,
          owner_id,
          project_id
        `)
        .eq('project_id', id)
        .order('deadline', { ascending: true });

      // Fetch user info manually if needed
      if (data && Array.isArray(data)) {
        const ownerIds = [...new Set(data.map(s => s.owner_id).filter(Boolean))];
        const { data: users, error: usersError } = await supabase
          .from('users')
          .select('id, display_name, email')
          .in('id', ownerIds);

        if (usersError) console.error('Error loading users:', usersError);

        const userMap = Object.fromEntries((users || []).map(u => [u.id, u]));
        data.forEach(s => {
          s.owner = userMap[s.owner_id] || null;
        });
      }

      if (error) console.error('Error loading submittals:', error);
      setSubmittals(data || []);
    };
    fetchSubmittals();
  }, [id]);

  const getRowStyleByDeadline = (deadline) => {
    if (!deadline) return {};
    const daysRemaining = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 7) return { backgroundColor: '#ffdddd' };
    if (daysRemaining <= 14) return { backgroundColor: '#fff4e5' };
    return {};
  };

  const submittalRows = useMemo(() => {
    return submittals.map((s) => {
      const daysRemaining = s.deadline
        ? Math.ceil((new Date(s.deadline) - new Date()) / (1000 * 60 * 60 * 24))
        : null;

      return {
        submittal: s.name || '—',
        owner: s.owner?.display_name || s.owner?.email || '—',
        deadline: s.deadline
          ? new Date(s.deadline).toLocaleDateString()
          : '—',
        submittal_date: s.submittal_date
          ? new Date(s.submittal_date).toLocaleDateString()
          : '—',
        days_to_deadline: daysRemaining !== null ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'}` : '—',
        rowSx: getRowStyleByDeadline(s.deadline),
      };
    });
  }, [submittals, project?.name]);

  useEffect(() => {
    const fetchProject = async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single();
      if (error) console.error('Error loading project:', error);
      else setProject(data);
    };

    const fetchRecords = async () => {
      const { data, error } = await supabase
        .from('project_records')
        .select('*')
        .eq('project_id', id)
        .order('application_date', { ascending: false });
      if (error) console.error('Error loading records:', error);
      else setRecords(data);
    };

    fetchProject();
    fetchRecords();
  }, [id]);

  useEffect(() => {
    if (!project?.client_id) return;

    const fetchClientName = async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('name')
        .eq('id', project.client_id)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching client name:', error);
      } else {
        setClientName(data?.name || '');
      }
    };

    fetchClientName();
  }, [project]);

  useEffect(() => {
    const fetchClientDates = async () => {
      const { data, error } = await supabase
        .from('client_dates')
        .select('date, description')
        .eq('project_id', id)
        .order('date', { ascending: true });
      if (error) console.error('Error loading client dates:', error);
      else setClientDates(data);
    };
    fetchClientDates();
  }, [id]);

  if (!project) return <Typography level="h4">Loading project...</Typography>;

  // Compute statusCounts for records
  const statusCounts = { 'In Progress': 0, 'In Review': 0, 'Active': 0 };
  records.forEach(record => {
    const { start_date, application_date, issue_date, expiration_date } = record;

    if (start_date && application_date && !issue_date && !expiration_date) {
      statusCounts['In Review']++;
    } else if (start_date && !application_date && !issue_date && !expiration_date) {
      statusCounts['In Progress']++;
    } else if (issue_date && expiration_date) {
      statusCounts['Active']++;
    } else if (expiration_date && !issue_date) {
      statusCounts['Active']++;
    }
  });

  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        label: 'Permit Count',
        data: Object.values(statusCounts),
        backgroundColor: ['#00A884', '#00796B', '#B2DFDB'],
      },
    ],
  };

  // Compute Project Intelligence values

  // Submittals overview
  const totalSubmittals = submittals.length;

  const nextSubmittal = submittals
    .filter(s => s.deadline)
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))[0] || null;


  // Permit summary
  const today = new Date();
  const activePermitsCount = records.filter(r => {
    const issueDate = r.issue_date ? new Date(r.issue_date) : null;
    const expirationDate = r.expiration_date ? new Date(r.expiration_date) : null;
    return issueDate && (!expirationDate || expirationDate > today);
  }).length;

  const expiringSoonCount = records.filter(r => {
    if (!r.expiration_date) return false;
    const expirationDate = new Date(r.expiration_date);
    const inNext30Days = expirationDate > today && expirationDate <= new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    return inNext30Days;
  }).length;

  const permitsWithExpiration = records.filter(r => r.expiration_date).sort((a, b) => new Date(a.expiration_date) - new Date(b.expiration_date));
  const earliestExpiration = permitsWithExpiration.length > 0 ? permitsWithExpiration[0] : null;

  return (
    <PageLayout title="Project Details">
      <Box sx={{ mt: 3, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }}>
            <Typography level="h3" mb={1}>{project.name}</Typography>
            <Typography level="body2" mb={2} sx={{ color: '#00A884' }}>
              Project Number: {project.project_number || '—'}
            </Typography>
            <Typography level="body2" sx={{ color: '#00A884' }}>
              Client: {clientName || '—'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Card sx={{ flex: 1, minWidth: 280, p: 2, backgroundColor: 'success.softBg', borderColor: 'success.softBorder', borderStyle: 'solid', borderWidth: 1 }}>
            <Typography level="h5" mb={1}>Submittals Overview</Typography>
            <Typography>Total Submittals: {totalSubmittals}</Typography>
            <Typography>
              Next Submittal Due: {nextSubmittal ? `${nextSubmittal.name} (${new Date(nextSubmittal.deadline).toLocaleDateString()})` : '—'}
            </Typography>
          </Card>
          <Card sx={{ flex: 1, minWidth: 280, p: 2, backgroundColor: 'success.plainHoverBg', borderColor: 'success.plainHoverBorder', borderStyle: 'solid', borderWidth: 1 }}>
            <Typography level="h5" mb={1}>Permit Summary</Typography>
            <Typography>Total Active Permits: {activePermitsCount}</Typography>
            <Typography>Permits Expiring in 30 Days: {expiringSoonCount}</Typography>
            <Typography>
              Earliest Expiration: {earliestExpiration ? `${earliestExpiration.name} (${new Date(earliestExpiration.expiration_date).toLocaleDateString()})` : '—'}
            </Typography>
          </Card>
        </Box>
      </Box>

      {clientDates.length > 0 && (
        <Box sx={{ mt: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'md', backgroundColor: '#f7f9fc' }}>
          <Typography level="h4" mb={2} sx={{ color: 'success.700' }}>Key Client Dates</Typography>
          {clientDates.map((item, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography level="body-md" fontWeight="md">
                {new Date(item.date + 'T00:00:00').toLocaleDateString(undefined, {
                  year: 'numeric', month: 'short', day: 'numeric'
                })}: <Typography component="span" level="body-md">{item.description}</Typography>
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Typography level="h4" mt={4} mb={1}>Associated Permits and Bonds</Typography>

      <StyledTable
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'record_type', label: 'Type' },
          { key: 'agency', label: 'Agency' },
          { key: 'status', label: 'Status' },
          { key: 'application_date', label: 'Application Date' },
          { key: 'expiration_date', label: 'Expiration' },
          { key: 'details', label: 'Details' },
        ]}
        rows={records.map((record) => {
          const expiresSoon =
            record.expiration_date &&
            new Date(record.expiration_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

          return {
            name: record.name,
            record_type: record.record_type,
            agency: record.agency || '—',
            status: record.status || '—',
            application_date: record.application_date || '—',
            expiration_date: record.expiration_date || '—',
            details: (
              <IconButton
                component={Link}
                to={`/permits/${record.id}`}
                size="sm"
                color="neutral"
                variant="outlined"
              >
                <InfoOutlinedIcon />
              </IconButton>
            ),
            rowSx: expiresSoon ? { backgroundColor: '#ffe5e5' } : {},
          };
        })}
      />
      <Typography level="h4" mt={4} mb={1}>Submittals</Typography>

      <StyledTable
        columns={[
          { key: 'submittal', label: 'Submittal' },
          { key: 'owner', label: 'Owner' },
          { key: 'deadline', label: 'Deadline' },
          { key: 'submittal_date', label: 'Submittal Date' },
          { key: 'days_to_deadline', label: 'Days Remaining' },
        ]}
        rows={submittalRows}
      />
    </PageLayout>
  );
};

export default ProjectDetail;